import React, { FC, createRef, useState, Fragment, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { FormInstance } from 'antd/es/form'
import { useHistory, useLocation } from 'react-router-dom'
import {
    Space, Form, Input, Button, Typography, List, Skeleton, Modal, Select,
    Collapse, Pagination, Card, notification, Popover, DatePicker
} from 'antd'
import {
    PlusOutlined, CheckCircleOutlined, FormOutlined, FileSearchOutlined, DeleteOutlined, SmileOutlined,
    PlusCircleOutlined
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import type { IAppState } from '@/store/reducers'
import type { IKeyRoadMap } from '@/store/models/tableEdit.model'
import { getErrorStr } from '@/store/sagas/table.saga'
import { requestFail } from '@/store/actions/common.action'
import { addRoadMap, editRoadMap, delRoadMap, getRoadMapById } from '@/services/table.service'
import NoSpaceInput from '@/components/noSpaceInput'
import { getKeyRoadMap, getWorkgroup, setKeyRoadMap, setKeyRoadMapPagination } from '@/store/actions/tableEdit.action'
import PageBreadcrumb from '@/components/pageBreadcrumb'
import Nav from '@/components/nav'

const KeyRoadMapEdit: FC = () => {
    const history = useHistory()
    const locationObj = useLocation()
    const dispatch = useDispatch()
    const { t, i18n } = useTranslation()
    // 第一次获取到 workgroup 时初始化查询 roadMap
    const [isInit, setIsInit] = useState(true)
    // 当点击搜索按钮时，不切换 collapse
    const [isSearch, setIsSearch] = useState(true)
    const [curSelItem, setCurSelItem] = useState<IKeyRoadMap | null>(null)
    const [curWorkgroup, setCurWorkgroup] = useState<number | null>(null)
    const [formDataId, setFormDataId] = useState<number | null>(null)
    const [collapseValue, setCollapseValue] = useState<string[] | string>(['2'])
    const [years, setYears] = useState<IKeyRoadMap[]>([])
    const [editStatus, setEditStatus] = useState(0) // 0 新增 1 编辑 2 查看
    const [curYearCollapse, setCurYearCollapse] = useState<string[] | string>([])
    const [showAddYear, setShowAddYear] = useState<boolean>(false)
    const route = useSelector((state: IAppState) => state.router)
    const authInfo = useSelector((state: IAppState) => state.auth.authInfo)
    const tableEditData = useSelector((state: IAppState) => state.tableEditData.table1EditData)
    const workgroupList = useSelector((state: IAppState) => state.tableEditData.workgroupList)
    const keyRoadMap = useSelector((state: IAppState) => state.tableEditData.keyRoadMap)
    const keyRoadMapPagination = useSelector((state: IAppState) => state.tableEditData.keyRoadMapPagination)
    const formRef = createRef<FormInstance>()
    const formRef2 = createRef<FormInstance>()
    const locationSearch = useRef<any>({})
    const { Text } = Typography
    const { Option } = Select
    const { Panel } = Collapse

    const layout = {
        labelCol: { span: 6 },
        wrapperCol: { span: 10 },
    }
    const tailLayout = {
        wrapperCol: { offset: 6, span: 10 },
    }

    const popMsg = (msg: string) => {
        const args = {
            message: msg,
            description: '',
            icon: <CheckCircleOutlined style={{ color: '#08993f' }} />,
            duration: 3,
        }
        notification.open(args)
    }

    const onReset = () => {
        formRef.current?.resetFields()
        // 收缩页未展开时，set 无效
        // formRef.current?.setFieldValue('workGroupId', curWorkgroup)
        setYears([])
        setFormDataId(null)
        setCurSelItem(null)
        setEditStatus(0)
        // dispatch(push('/keyRoadMapEdit', null))
    }
    const onReset2 = () => {
        onReset()
        formRef2.current?.resetFields()
    }
    const onFinish = async (values: any) => {
        console.log(values)
        const yearArr: IKeyRoadMap[] = []
        const obj: {[number: number]: any} = {}
        // 年字段名都加了 "_"
        Object.keys(values).forEach(item => {
            if (item.includes('_')) {
                const _yearArr: string[] = item.split('_')
                const field = _yearArr.pop() as string
                const year = +_yearArr[0]
                if (!(year in obj)) {
                    obj[year] = {}
                    yearArr.push(obj[year])
                    obj[year].parentId = values.id
                    obj[year].workGroupId = values.workGroupId
                    obj[year].year = year
                }
                obj[year][field] = values[item] || ''
            }
        })
        try {
            setIsSearch(false)
            if (values.id) {
                // 调用修改接口
                await editRoadMap({
                    version: values.version,
                    id: values.id,
                    parentId: values.parentId,
                    projectInfo: values.projectInfo,
                    releaseStatus: values.releaseStatus,
                    techName: values.techName,
                    techPtn: values.techPtn,
                    workGroupId: values.workGroupId,
                    year: values.year,
                    phases: yearArr
                })
                
                popMsg(t('save') + t('success'))
                history.goBack()
                return
            } else {
                // 调用添加接口
                const res = await addRoadMap({
                    projectInfo: values.projectInfo,
                    releaseStatus: values.releaseStatus || '',
                    techName: values.techName || '',
                    techPtn: values.techPtn || '',
                    workGroupId: values.workGroupId
                })
                // 追加到后面
                dispatch(setKeyRoadMap([...keyRoadMap, {
                    id: res,
                    parentId: values.parentId,
                    projectInfo: values.projectInfo,
                    releaseStatus: values.releaseStatus,
                    techName: values.techName,
                    techPtn: values.techPtn,
                    workGroupId: values.workGroupId,
                    year: values.year,
                    phases: yearArr
                }]))
            }
            onReset()
            popMsg(t('save') + (i18n.language !== 'zh' ? ' ' : '') + t('success'))
            // 直接追加到列表

            // 查询当前类
            setCurSelItem(null)
        } catch (error: any) {
            const str = getErrorStr(error)
            dispatch(requestFail(str))
        }
    }

    const onSearchFinish = (values: any) => {
        setIsSearch(true)
        // 获取 keyRoadMapList
        dispatch(getKeyRoadMap(values.workGroupId || ''))
        dispatch(setKeyRoadMapPagination({
            pageIndex: 1
        }))
        setCurSelItem(null)
        onReset2()
    }

    const onFinishFailed = ({ errorFields }: { errorFields: any}) => {
        console.log(errorFields)
        if (errorFields.length) {
            const args = {
                message: t('FinishFailed'),
                description: '',
                icon: <SmileOutlined style={{ color: '#e1931e' }} />,
                duration: 3,
            }
            notification.open(args)
        }
    }

    const onEditKeyRoadMap = (item: IKeyRoadMap, view?: string) => {
        console.log('onEditKeyRoadMap item: ', item)
        setCurWorkgroup(item.workGroupId)
        if (view === 'view') {
            setEditStatus(2)
        } else {
            setEditStatus(1)
        }
        const curYear = new Date().getFullYear() - 1
        // 当为搜索状态时，不切换 collapse
        setIsSearch(false)
        setCollapseValue('1')
        item.phases?.sort((a, b) => (a.year || 0) - (b.year || 0))
        item.phases?.forEach(phase => {
            const year = phase.year || curYear
            item[`${year}_id`] = phase.id
            item[`${year}_version`] = phase.version
            item[`${year}_techName`] = phase.techName
            item[`${year}_techPtn`] = phase.techPtn
            item[`${year}_projectInfo`] = phase.projectInfo
            item[`${year}_releaseStatus`] = phase.releaseStatus
            item[year] = year
        })
        setCurSelItem(item)
        const yearArr = item.phases?.map(phase => ({
            version: item.version,
            parentId: item.id,
            projectInfo: phase.projectInfo || '',
            releaseStatus: phase.releaseStatus,
            techName: phase.techName,
            techPtn: phase.techPtn,
            workGroupId: item.workGroupId,
            year: phase.year || curYear
        })) || []
        setYears(yearArr)
        formRef.current?.setFieldsValue(item)
        // 展开第一个年份
        if (yearArr.length) {
            setCurYearCollapse(String(yearArr[0].year))
        }
        
        window.scrollTo(0, 0)
    }
    const onDelKeyRoadMap = (item: IKeyRoadMap) => {
        console.log('item: ', item)
        Modal.confirm({
            title: t('confirmDel'),
            centered: true,
            okText: t('confirm'),
            cancelText: t('cancel'),
            async onOk () {
                try {
                    await delRoadMap(item.id as number)
                    popMsg(t('delete') + (i18n.language !== 'zh' ? ' ' : '') + t('success'))
                    setIsSearch(true)
                    // 直接本地删除
                    dispatch(setKeyRoadMap(keyRoadMap.filter(keyRoadMapItem => keyRoadMapItem.id !== item.id)))
                    setCollapseValue('2')
                    onReset()
                } catch (error: any) {
                    const str = getErrorStr(error)
                    dispatch(requestFail(str))
                }
            }
        })
    }
    const onChangeCollapse = (e: string[] | string) => {
        setCollapseValue(e)
    }

    const onAddYear = (addYear?: number) => {
        console.log('years: ', years)
        years.sort((a: IKeyRoadMap, b: IKeyRoadMap) => (a?.year || 0) - (b?.year || 0))
        const year = addYear ? addYear : (years.length ? (years[years.length - 1].year as number) + 1 : new Date().getFullYear() - 1)
        years.push({
            parentId: curSelItem?.id,
            projectInfo: '',
            releaseStatus: '',
            techName: '',
            techPtn: '',
            workGroupId: curSelItem?.workGroupId as number,
            year,
            editStatus: 0
        })
        setYears([...years])
        onChangeYearCollapse(String(year))
        // 清空form input上一条数据遗留显示
        const curYearInput: Record<string, any> = {}
        curYearInput[`${year}_id`] = undefined
        curYearInput[`${year}_techName`] = ''
        curYearInput[`${year}_techPtn`] = ''
        curYearInput[`${year}_projectInfo`] = ''
        curYearInput[`${year}_releaseStatus`] = ''
        curYearInput[year] = year
        formRef.current?.setFieldsValue(curYearInput)
    }
    const onChangeYearCollapse = (e: string[] | string) => {
        setCurYearCollapse(e)
    }

    const getRoadMapInfo = async (id?: number, view?: string) => {
        const res = await getRoadMapById(id || locationSearch.current.roadMapId)
        setFormDataId(res.id)
        onEditKeyRoadMap({
            id: res.id,
            projectInfo: res.projectInfo || '',
            releaseStatus: res.releaseStatus,
            techName: res.techName,
            techPtn: res.techPtn,
            workGroupId: res.workGroup.id,
            phases: res.phases,
            year: res.year || 0,
            version: res.version
        }, view)
    }

    const routes = [
        {
            path: '/indexPage',
            breadcrumbName: t('indexPage'),
        },
        {
            path: '/keyRoadMap',
            breadcrumbName: t('technology roadmap') + (i18n.language !== 'zh' ? ' ' : '') + t('list'),
        },
        {
            path: '/workgroupEdit',
            breadcrumbName: t('manage') + (i18n.language !== 'zh' ? ' ' : '') + t('technology roadmap'),
        },
    ]
    useEffect(() => {
        console.log('route: ', route, locationObj)
        // 获取 workgroupList
        if (!workgroupList.length) {
            dispatch(getWorkgroup())
        }
        const routeParams = new URLSearchParams(location.search)
        const roadMapIdParam = routeParams.get('roadMapId')
        if (roadMapIdParam) {
            setFormDataId(+roadMapIdParam)
        }
        locationSearch.current = {
            roadMapId: +(roadMapIdParam || '')
        }
        setIsInit(false)
        dispatch(getKeyRoadMap(+(roadMapIdParam || '') || {
            workGroupId: '',
            pageNo: keyRoadMapPagination.pageIndex,
            pageSize: keyRoadMapPagination.pageSize
        }))
    }, [])
    useEffect(() => {
        // 获取到workgroup后初始获取 keyRoadMapList
        if (!isInit && workgroupList.length) {
            console.log('locationSearch.current.roadMapId: ', locationSearch.current.roadMapId)
            
            if (!(locationObj.pathname.includes('Add'))
                && locationSearch.current.roadMapId
            ) {
                getRoadMapInfo()
            }
        }
    }, [workgroupList, isInit])
    useEffect(() => {
        // 如果是编辑，则不用切换
        if (!locationObj.pathname.includes('Add')) {
            return
        }
        if (isSearch) {
            setCollapseValue('2')
            return
        }
        // 没有 roadMap 时显示新增，否则显示搜索
        if (keyRoadMap.length) {
            setCollapseValue('2')
        } else {
            setCollapseValue('1')
        }
    }, [keyRoadMap])
    // 编辑时设置 formData
    useEffect(() => {
        if (collapseValue === '1') {
            if (curSelItem) {
                formRef.current?.setFieldsValue(curSelItem)
            } else {
                formRef.current?.resetFields()
            }
        }
    }, [curSelItem, collapseValue])
    return (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Nav dispatch={dispatch} />
            <PageBreadcrumb routerParam={routes}
                history={history}
                route={route}
                dispatch={dispatch}
            />
            <Collapse collapsible="header" defaultActiveKey={collapseValue} className="edit_table"
                style={{ paddingTop: 0 }}
                accordion
                activeKey={collapseValue}
                onChange={onChangeCollapse}
            >
                {/* 新增、编辑、查看 需要验证权限 */}
                {authInfo &&
                    ((!formDataId && Reflect.has(authInfo, 'addRoadMap')) ||
                    (formDataId &&
                        (Reflect.has(authInfo, 'editRoadMap') || Reflect.has(authInfo, 'viewRoadMap'))
                    ))
                && <Panel key="1"
                    header={curSelItem ? (editStatus === 2 ? t('View Roadmap') : t('Modify Roadmap')) : t('Add Roadmap')}
                    forceRender
                    extra={
                        // 非编辑工作组路线图状态、有新增权限、非新增的状态才显示
                        locationObj.pathname.includes('Add') &&
                        authInfo && Reflect.has(authInfo, 'addRoadMap') &&
                        editStatus !== 0 &&
                        <Text title={t('Add Roadmap')}
                            className='txt_blue hand'
                            onClick={() => {
                                setEditStatus(0)
                                onReset()
                                setCollapseValue('1')
                            }}
                        ><PlusCircleOutlined style={{ fontSize: 18 }} /></Text>
                    }
                >
                    <Form {...layout} ref={formRef} name="control-ref"
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                    >
                        <Fragment key={tableEditData.key}>
                            <Form.Item name="workGroupId" label={t('Work Group')}
                                rules={[{ required: true, message: t('Work Group Required') }]}
                            >
                                {
                                    editStatus === 0 ? (
                                        <Select allowClear placeholder={t('Work Group Placeholder')}>
                                            {
                                                workgroupList.map(item => {
                                                    return <Option key={item.id} value={item.id}>{item.groupName}</Option>
                                                })
                                            }
                                        </Select>
                                    ) : <div>{ 
                                        workgroupList.find(workgroupItem => workgroupItem.id === curSelItem?.workGroupId)?.groupName
                                    }</div>
                                }
                            </Form.Item>
                            <Form.Item name="id" hidden><Input /></Form.Item>
                            <Form.Item name="version" hidden><Input /></Form.Item>
                            <Form.Item name="parentId" hidden><Input /></Form.Item>
                            <Form.Item name="year" hidden><Input /></Form.Item>
                            <Form.Item name="techName" label={t('Tech Name')}
                                rules={[{ required: true, message: t('Tech Name Required') }]}
                            >
                                {
                                    editStatus !== 2 ? (
                                        <NoSpaceInput placeholder={t('Tech Name Placeholder')} />
                                    ) : <div>{curSelItem?.techName}</div>
                                }
                            </Form.Item>
                            <Form.Item name="techPtn" label={t('PTN')}>
                                {
                                    editStatus !== 2 ? (
                                        <NoSpaceInput placeholder={t('PTN Placeholder')} maxLength={10} />
                                    ) : <div>{curSelItem?.techPtn}</div>
                                }
                            </Form.Item>
                            <Form.Item name="projectInfo" label={t('Project Info')}>
                                {
                                    editStatus !== 2 ? (
                                        <NoSpaceInput placeholder={t('Project Info Placeholder')} />
                                    ) : <div>{curSelItem?.projectInfo}</div>
                                }
                            </Form.Item>
                        </Fragment>
                        {
                            years.length ? (
                                <Collapse collapsible="header" className="edit_subtable"
                                    style={{ paddingTop: 0 }}
                                    accordion
                                    activeKey={curYearCollapse}
                                    onChange={onChangeYearCollapse}
                                >
                                    {
                                        years.map(item => (
                                            <Panel header={`${item.year}${t('year')}`}
                                                forceRender
                                                key={item.year as number}
                                            >
                                                <Form.Item name={`${item.year}_id`} hidden><Input /></Form.Item>
                                                <Form.Item name={`${item.year}_version`} hidden><Input /></Form.Item>
                                                <Form.Item name={`${item.year}_techPtn`} label={t('PTN')} wrapperCol={{ span: 18 }}
                                                    rules={[{ required: true, message: t('PTN Required') }]}
                                                >
                                                    { item.editStatus === 0
                                                        ? <NoSpaceInput value={item.techPtn} placeholder={t('PTN Placeholder')} />
                                                        : <div>{item.techPtn}</div>
                                                    }
                                                </Form.Item>
                                                <Form.Item name={`${item.year}_techName`} label={t('Tech Name')}
                                                    rules={[{ required: true, message: t('Tech Name Required') }]}
                                                    wrapperCol={{ span: 18 }}
                                                >
                                                    {
                                                        editStatus !== 2 ? (
                                                            <NoSpaceInput value={item.techName} placeholder={t('Tech Name Placeholder')} />
                                                        ) : <div>{item.techName}</div>
                                                    }
                                                </Form.Item>
                                                <Form.Item name={`${item.year}_projectInfo`} label={t('Project Info')} wrapperCol={{ span: 18 }}>
                                                    {
                                                        editStatus !== 2 ? (
                                                            <NoSpaceInput value={item.projectInfo} placeholder={t('Project Info Placeholder')} />
                                                        ) : <div>{item.projectInfo}</div>
                                                    }
                                                </Form.Item>
                                                <Form.Item name={`${item.year}_releaseStatus`} label={t('Release Status')} wrapperCol={{ span: 18 }}>
                                                    {
                                                        editStatus !== 2 ? (
                                                            <Select allowClear value={item.releaseStatus} placeholder={t('Release Status Placeholder')}>
                                                                {
                                                                    ['OPEN', 'CLOSED'].map(item => {
                                                                        return <Option key={item} value={item}>{item}</Option>
                                                                    })
                                                                }
                                                            </Select>
                                                        ) : <div>{item.releaseStatus}</div>
                                                    }
                                                </Form.Item>
                                            </Panel>
                                        ))
                                    }
                                </Collapse>
                            ) : null
                        }
                        {/* 新增时不能添加年，因为年要有parentId */}
                        {curSelItem && editStatus !== 2 && (
                            <Form.Item {...tailLayout} style={{ textAlign: 'center' }}>
                                {!years.length
                                    ? (
                                        <Popover visible={showAddYear} trigger="click"
                                            onVisibleChange={setShowAddYear}
                                            content={
                                                <DatePicker allowClear picker="year"
                                                    disabledDate={current => {
                                                        let _year = new Date().getFullYear() - 1
                                                        if (years.length) {
                                                            _year = +years[years.length - 1] + 1
                                                        }
                                                        return current && current.year() < _year
                                                    }}
                                                    onChange={(date, dateString) => {
                                                        if (date) {
                                                            onAddYear(+dateString)
                                                        }
                                                    }}
                                                />
                                            }
                                        >
                                            <Button icon={<PlusOutlined />}>{t('add')}{t('year')}</Button>
                                        </Popover>
                                    )
                                    : (
                                        <Button icon={<PlusOutlined />} onClick={() => onAddYear()}>{t('add')}{t('year')}</Button>
                                    )
                                }
                            </Form.Item>
                        )}
                        {
                            editStatus !== 2 && (
                                <Form.Item {...tailLayout} style={{ textAlign: 'center' }}>
                                    <Space size={20}>
                                        <Button type="primary" htmlType="submit">
                                            {t('submit')}
                                        </Button>
                                        {editStatus === 0 && (<Button htmlType="button" onClick={onReset}>
                                            {t('reset')}
                                        </Button>)}
                                    </Space>
                                </Form.Item>
                            )
                        }
                    </Form>
                </Panel>}
                {locationObj.pathname.includes('Add') && 
                    <Panel header={t('Search Roadmap')} key="2">
                        <Form {...layout} ref={formRef2} name="control-ref" onFinish={onSearchFinish}>
                            <Form.Item name="workGroupId" label={t('Work Group')}
                                style={{ marginTop: 10 }}
                            >
                                <Select allowClear placeholder={t('Work Group Placeholder')}
                                    onChange={e => {
                                        if (e) {
                                            setCurWorkgroup(e)
                                        }
                                    }}
                                >
                                    {
                                        workgroupList.map(item => {
                                            return <Option key={item.id} value={item.id}>{item.groupName}</Option>
                                        })
                                    }
                                </Select>
                            </Form.Item>
                            <Form.Item {...tailLayout} style={{ textAlign: 'center' }}>
                                <Space size={20}>
                                    <Button type="primary" htmlType="submit">
                                        {t('search')}
                                    </Button>
                                    <Button htmlType="button" onClick={onReset2}>
                                        {t('reset')}
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Panel>
                }
            </Collapse>
            {locationObj.pathname.includes('Add') &&
                <Card title={t('Roadmap List')} className="road_map_ls">
                    <Space direction="vertical" size={20} style={{ width: '100%' }}>
                        <List
                            itemLayout="horizontal"
                            dataSource={keyRoadMap}
                            renderItem={item => (
                                <List.Item
                                    actions={!item.loading && authInfo ? [
                                        <Text title={t('edit')}
                                            onClick={() => getRoadMapInfo(item.id)}
                                            className={Reflect.has(authInfo, 'editRoadMap') ? 'txt_blue hand' : 'hide'} 
                                        ><FormOutlined style={{ fontSize: 16 }} /></Text>,
                                        <Text title={t('view')}
                                            onClick={() => getRoadMapInfo(item.id, 'view')}
                                            className={Reflect.has(authInfo, 'viewRoadMap') ? 'txt_blue hand' : 'hide'} 
                                        ><FileSearchOutlined style={{ fontSize: 16 }} /></Text>, 
                                        <Text title={t('delete')}
                                            className={Reflect.has(authInfo, 'delRoadMap') ? 'txt_red hand' : 'hide'} 
                                            onClick={() => onDelKeyRoadMap(item)}
                                        ><DeleteOutlined style={{ fontSize: 16 }} /></Text>
                                    ] : []}
                                >
                                    <Skeleton avatar title={false} loading={item.loading} active>
                                        <List.Item.Meta
                                            title={
                                                <Space style={{ alignItems: 'flex-end' }}>
                                                    <h3>{item.techName}</h3>
                                                    <span style={{ fontSize: 12, color: '#999' }}>{workgroupList.find(workgroup => workgroup.id === item.workGroupId)?.groupName}</span>
                                                </Space>
                                            }
                                        />
                                    </Skeleton>
                                </List.Item>
                            )}
                        />
                        {keyRoadMapPagination.total !== 0 && (
                            <Pagination className='road_map_pagination'
                                defaultCurrent={keyRoadMapPagination.pageIndex}
                                current={keyRoadMapPagination.pageIndex}
                                total={keyRoadMapPagination.total}
                                pageSize={keyRoadMapPagination.pageSize}
                                onChange={(page: number) => {
                                    dispatch(getKeyRoadMap({
                                        workGroupId: curWorkgroup || '',
                                        pageNo: page,
                                        pageSize: keyRoadMapPagination.pageSize
                                    }))
                                    dispatch(setKeyRoadMapPagination({
                                        pageIndex: page
                                    }))
                                }}
                            />
                        )}
                    </Space>
                </Card>
            }
        </Space>
    )
}

export default KeyRoadMapEdit
